import {
  Button,
  DateRangeSelector,
  DateRangeType,
  Dropdown,
  ENUM_DATE_RANGE,
  Input,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { BackIcon, ClockIcon, DeleteIcon, AddIcon, ErrorIcon } from 'cokitchen-icons';
import { ReactComponent as ThreeDotsIcon } from 'assets/icons/three_dots.svg';
import { ReactComponent as CancelIcon } from 'assets/icons/cancel.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  filterDropdownOptions,
  getTableHeaders,
  ridersAuthorizationStatus,
  ridersPillDetails
} from './utils';
import { apiService } from 'utils/api';
import { ENUM_AUTHORIZE_TYPE, ENUM_RIDER_STATUS } from 'utils/enum';
import { ExternalRiderType } from 'models/external-rider.model';
import { toast } from 'react-toastify';
import RejectExternalRiderModal from 'components/logistics/external-riders/reject-external-rider-modal';
import ExternalRiderDetailsModal from 'components/logistics/external-riders/external-rider-details-modal';
import ScheduledRiderModal from 'components/logistics/external-riders/schedule-rider-modal';
import ScheduledRiderTableData from './partials/scheduled-rider-table-data';
import ExternalRiderTableData from './partials/external-rider-table-data';
import { withDropdownList } from 'components/base/dropdown-list';
import { handleApiError } from 'utils/functions';

export const getDefaultRiderOptions = (
  authorizeType: ENUM_AUTHORIZE_TYPE,
  loading: boolean,
  setOpenRejectRiderModal: (val: boolean) => void,
  setOpenScheduledRiderModal: (val: boolean) => void,
  setSelectedRiders: (riders: ExternalRiderType[]) => void,
  handleAuthorizeApplication: (riders: ExternalRiderType[], type: ENUM_AUTHORIZE_TYPE) => void
) => [
  {
    name: 'Approve rider',
    id: ENUM_RIDER_STATUS.APPROVED,
    icon: <AddIcon />,
    onClick: (rider: ExternalRiderType) => {
      handleAuthorizeApplication([rider], ENUM_AUTHORIZE_TYPE.APPROVE);
    },
    className: `text-sm ${authorizeType === ENUM_AUTHORIZE_TYPE.APPROVE ? 'opacity-50' : ''}`
  },
  {
    name: 'Reject rider',
    id: ENUM_RIDER_STATUS.REJECTED,
    icon: <ErrorIcon />,
    onClick: (rider: ExternalRiderType) => {
      setOpenRejectRiderModal(true);
      setSelectedRiders([rider]);
    },
    className: 'text-[#E60A2B] text-sm'
  },
  {
    name: 'Schedule rider',
    id: ENUM_RIDER_STATUS.SCHEDULED,
    icon: <ClockIcon />,
    onClick: (rider: ExternalRiderType) => {
      setSelectedRiders([rider]);
      setOpenScheduledRiderModal(true);
    },
    className: 'text-gray-6/70'
  },
  {
    name: 'Delete',
    id: 'delete',
    icon: <DeleteIcon />,
    onClick: (rider: ExternalRiderType) => {
      handleAuthorizeApplication([rider], ENUM_AUTHORIZE_TYPE.DELETE);
    },
    className: `text-[#E60A2B] text-sm ${
      authorizeType === ENUM_AUTHORIZE_TYPE.DELETE || loading ? 'opacity-50 cursor-not-allowed' : ''
    }`
  }
];

const DropdownList = withDropdownList<ReturnType<typeof getDefaultRiderOptions>[0]>();

const ExternalRiders = () => {
  const [loading, setLoading] = useState(false);
  const [authorizeType, setAuthorizeType] = useState<ENUM_AUTHORIZE_TYPE>();
  const [openScheduledRiderModal, setOpenScheduledRiderModal] = useState(false);
  const [allExternalRiders, setAllExternalRiders] = useState<ExternalRiderType[]>([]);
  const [selectedRiders, setSelectedRiders] = useState<ExternalRiderType[]>([]);
  const [openRejectRiderModal, setOpenRejectRiderModal] = useState(false);
  const [viewRiderDetails, setViewRiderDetails] = useState(false);

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const status = searchParams.get('status') ?? 'ALL';

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [dateRange, setDateRange] = useState<DateRangeType>();

  const [headers, setHeaders] = useState(['']);

  const handleAuthorizeApplication = async (
    riders: ExternalRiderType[],
    action: ENUM_AUTHORIZE_TYPE
  ) => {
    setAuthorizeType(action);
    const url = `rider-applications/bulk`;

    try {
      await apiService.post(url, {
        baseUrl: process.env.REACT_APP_AUTH_API_URL,
        data: {
          application_ids: riders?.map((rider) => rider?.application_id),
          action
        }
      });
      toast(<ToastMessage type='success' message={ridersAuthorizationStatus[action]} />);
      handleGetExternalRiders();
    } catch (err: any) {
      toast(<ToastMessage type='error' message={'An error occured'} />);
    } finally {
      setAuthorizeType(undefined);
      setSelectedRiders([]);
    }
  };

  const getSelectedPage = (val: { selected: number }) => {
    setPage(val.selected + 1);
  };

  const toggleAllRiders = () => {
    setSelectedRiders(selectedRiders.length > 0 ? [] : allExternalRiders);
  };

  const updateSingleRider = (appId: string) => {
    const hasRider = selectedRiders.some((rider) => rider.application_id === appId);
    if (hasRider) {
      setSelectedRiders(selectedRiders.filter((rider) => rider.application_id !== appId));
    } else {
      const toggledRider = allExternalRiders?.find((rider) => rider.application_id === appId);
      if (toggledRider) setSelectedRiders([...selectedRiders, toggledRider]);
    }
  };

  const handleGetExternalRiders = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('rider-applications', {
        params: {
          page,
          status_filter: status === 'ALL' ? '' : status,
          search: searchValue,
          start_date: dateRange?.date?.startDate,
          end_date: dateRange?.date?.endDate
        },

        baseUrl: process.env.REACT_APP_AUTH_API_URL
      });
      if (res.data?.results) {
        setAllExternalRiders(res.data.results);
        const totalNoOfPages = Math.ceil(res.data.total / 10);
        setPageCount(totalNoOfPages);
      }
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetExternalRiders();
  }, [page, searchValue, status, dateRange]);

  useEffect(() => {
    setHeaders(getTableHeaders(status));
  }, [status]);

  const isAllSelected = selectedRiders?.length === allExternalRiders?.length;
  const disableDeleteBtn =
    authorizeType === ENUM_AUTHORIZE_TYPE.APPROVE || authorizeType === ENUM_AUTHORIZE_TYPE.REJECT;
  const disableApproveBtn =
    authorizeType === ENUM_AUTHORIZE_TYPE.REJECT || authorizeType === ENUM_AUTHORIZE_TYPE.DELETE;
  const disableRejectbtn =
    authorizeType === ENUM_AUTHORIZE_TYPE.APPROVE || authorizeType === ENUM_AUTHORIZE_TYPE.DELETE;

  const defaultOptions = getDefaultRiderOptions(
    authorizeType as ENUM_AUTHORIZE_TYPE,
    loading,
    setOpenRejectRiderModal,
    setOpenScheduledRiderModal,
    setSelectedRiders,
    handleAuthorizeApplication
  );

  const handleDropdownList = (rider: ExternalRiderType) =>
    filterDropdownOptions(rider, defaultOptions);

  const hasApprovedOrRejected = selectedRiders.some(
    (rider) =>
      rider.status === ENUM_RIDER_STATUS.APPROVED || rider.status === ENUM_RIDER_STATUS.REJECTED
  );

  return (
    <main className='relative'>
      <section className='space-y-5'>
        <button
          onClick={() => navigate(-1)}
          className='inline-flex gap-1 items-center text-gray-8 text-sm font-medium'>
          <BackIcon /> Back to overview
        </button>
        <h2 className='text-2xl font-semibold'>External Riders</h2>
        <div className='flex items-center justify-between'>
          <div className='flex flex-wrap items-center gap-3'>
            {ridersPillDetails.map((pill) => (
              <button
                onClick={() => {
                  setSearchParams({ status: pill.id });
                  setSelectedRiders([]);
                }}
                key={pill.id}
                aria-label={`${pill.name} pill`}
                className={`text-sm font-medium  ${
                  pill.id === status ? 'bg-red-5 text-white' : 'bg-gray-1 text-gray-6/60'
                } px-4 p-1.5 rounded-2xl`}>
                {pill.name}
              </button>
            ))}
          </div>
          <div className='flex items-center gap-3'>
            <div className='mt-1.5'>
              <DateRangeSelector
                defaultRange={ENUM_DATE_RANGE.THIS_WEEK}
                onChange={setDateRange}
                className='h-10 py-0 text-sm border-gray-2 min-w-fit px-2 text-gray-6/70 gap-2 bg-transparent font-medium'
              />
            </div>
            <Input
              className='max-w-md w-full py-0 '
              inputClass='h-10 rounded py-0'
              placeholder='Search rider '
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        </div>
      </section>
      <section className='mt-16'>
        {allExternalRiders?.length === 0 && !loading ? (
          <div className='text-xl leading-[40vh] text-center'>No rider</div>
        ) : (
          <div className='no-scroll-bar whitespace-nowrap border border-gray-1/70 rounded-xl'>
            <Table
              isAllSelected={isAllSelected}
              onToggleAll={toggleAllRiders}
              headers={headers}
              loading={loading}
              onPageChange={getSelectedPage}
              pageCount={pageCount}
              canSelect>
              {allExternalRiders.map((rider, idx) => (
                <TableRow
                  isSelected={selectedRiders.some((selectedRider) =>
                    selectedRider.application_id.includes(rider.application_id)
                  )}
                  onToggle={() => updateSingleRider(rider.application_id)}
                  canSelect
                  className={`last:border-none $${
                    authorizeType === ENUM_AUTHORIZE_TYPE.APPROVE ? 'opacity-50' : ''
                  }`}
                  key={idx}>
                  {status === ENUM_RIDER_STATUS.SCHEDULED ? (
                    <ScheduledRiderTableData rider={rider} />
                  ) : (
                    <ExternalRiderTableData rider={rider} status={status as ENUM_RIDER_STATUS} />
                  )}

                  <TableData className='flex items-center gap-3'>
                    <button
                      onClick={() => {
                        setViewRiderDetails(true);
                        setSelectedRiders([rider]);
                      }}
                      className='text-red-5 underline underline-offset-2 text-xs font-medium'>
                      View Details
                    </button>
                  </TableData>

                  <TableData>
                    <Dropdown
                      content={
                        <button aria-label='Icon' className='text-gray-6/70'>
                          <ThreeDotsIcon />
                        </button>
                      }>
                      <DropdownList
                        list={handleDropdownList(rider)}
                        onSelect={(item) => item.onClick(rider)}
                      />
                    </Dropdown>
                  </TableData>
                </TableRow>
              ))}
            </Table>
          </div>
        )}
      </section>
      <div
        className={`
    fixed bottom-0 w-full flex justify-center z-50 transition-all duration-300 ease-in-out
    ${
      selectedRiders?.length > 0
        ? 'translate-y-2 opacity-100'
        : 'translate-y-full opacity-0 pointer-events-none'
    }
  `}>
        <div className='bg-white shadow-md rounded-full p-3 border border-gray-1/60 flex items-center gap-12 px-6 mb-6'>
          <span className='text-sm font-medium text-gray-5'>
            {selectedRiders?.length} rider(s) selected
          </span>
          <div className='flex items-center gap-5'>
            {!hasApprovedOrRejected && (
              <>
                <Button
                  variant='block'
                  color='light-gray'
                  className='py-1.5 px-3 text-xs font-semibold !text-gray-6/60 rounded-md gap-1 disabled:opacity-50'
                  disabled={disableApproveBtn}
                  loading={authorizeType === ENUM_AUTHORIZE_TYPE.APPROVE}
                  onClick={() =>
                    handleAuthorizeApplication(selectedRiders, ENUM_AUTHORIZE_TYPE.APPROVE)
                  }>
                  <CheckIcon />
                  Approve
                </Button>
                <Button
                  variant='block'
                  color='light-gray'
                  className='py-1.5 px-3 text-xs font-semibold !text-gray-6/60 rounded-md gap-1 disabled:opacity-50'
                  disabled={disableRejectbtn}
                  loading={authorizeType === ENUM_AUTHORIZE_TYPE.REJECT}
                  onClick={() => setOpenRejectRiderModal(true)}>
                  <CancelIcon />
                  Reject
                </Button>
              </>
            )}
            <Button
              variant='block'
              color='light-gray'
              disabled={disableDeleteBtn}
              loading={authorizeType === ENUM_AUTHORIZE_TYPE.DELETE}
              onClick={() => handleAuthorizeApplication(selectedRiders, ENUM_AUTHORIZE_TYPE.DELETE)}
              className='py-1.5 px-3 text-xs font-semibold !text-gray-6/60 rounded-md gap-1.5 disabled:opacity-50'>
              <DeleteIcon className='size-4' />
              Remove
            </Button>
            <Button
              variant='block'
              color='primary'
              onClick={() => setSelectedRiders([])}
              className='py-1.5 px-5 text-xs font-semibold rounded-md '>
              Done
            </Button>
          </div>
        </div>
      </div>

      {openRejectRiderModal && (
        <RejectExternalRiderModal
          show={openRejectRiderModal}
          onClose={() => {
            setOpenRejectRiderModal(false);
            setSelectedRiders([]);
          }}
          riders={selectedRiders}
          onSuccess={handleGetExternalRiders}
        />
      )}

      {viewRiderDetails && (
        <ExternalRiderDetailsModal
          onClose={() => {
            setViewRiderDetails(false);
            setSelectedRiders([]);
          }}
          onSuccess={handleGetExternalRiders}
          open={viewRiderDetails}
          rider={selectedRiders?.[0]}
        />
      )}

      {openScheduledRiderModal && (
        <ScheduledRiderModal
          onSuccess={handleGetExternalRiders}
          riders={selectedRiders}
          closeModal={() => {
            setOpenScheduledRiderModal(false);
            setSelectedRiders([]);
          }}
        />
      )}
    </main>
  );
};

export default ExternalRiders;
